import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Output,
    ViewChild,
    afterNextRender,
    inject,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslateModule } from '@ngx-translate/core';
import { AncCustomDateRangeComponent } from '../anc-custom-date-range/anc-custom-date-range.component';
import type {
    AncDatePickerPopupAlign,
    AncDatePickerSize,
    AncDatePickerValue,
} from '../anc-date-picker/anc-date-picker.models';
import { formatAncCreatedAtFilter } from './anc-created-at-filter.util';
import { AncFilterChipComponent } from './anc-filter-chip.component';
import {
    AncFilterChip,
    AncFilterChange,
    AncFilterOption,
} from './anc-filter.models';

let nextFilterDrawerId = 0;

/**
 * Chip filter bar with built-in date picker. Parent owns chip `values`; the bar
 * owns date-picker UI state and emits the API-ready `created_at` string.
 *
 * Whenever the chips don't fit on one line (OEM-7023), at any screen width, they move
 * into a slide-in drawer opened by a "Filters" button that shows the applied count. It
 * is the same chip DOM restyled by CSS, so values are unaffected by rotation or resize.
 */
@Component({
    selector: 'app-anc-filter-bar',
    standalone: true,
    imports: [
        CommonModule,
        A11yModule,
        TranslateModule,
        AncFilterChipComponent,
        AncCustomDateRangeComponent,
    ],
    templateUrl: './anc-filter-bar.component.html',
    styleUrls: ['./anc-filter-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AncFilterBarComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() chips: AncFilterChip[] = [];
    @Input() values: Record<string, any> = {};
    @Input() optionsByChip: Record<string, AncFilterOption[]> = {};
    @Input() loadingByChip: Record<string, boolean> = {};
    @Input() hasMoreByChip: Record<string, boolean> = {};
    /** Chip-only applied count; date filter is added internally for the badge. */
    @Input() appliedCount: number = 0;
    @Input() showClearAll: boolean = true;
    @Input() showCountBadge: boolean = true;
    @Input() showDateRange: boolean = false;
    /** Calendar popup alignment relative to the date field (`left` = start, `right` = end). */
    @Input() dateRangePopupAlign: AncDatePickerPopupAlign = 'left';
    /** Date field size — defaults to `small` to match filter chips in the bar. */
    @Input() dateRangeSize: AncDatePickerSize = 'small';
    /** Increment to reset date-picker UI when parent clears filters (e.g. route change). */
    @Input() set filterResetToken(value: number) {
        if (value > 0 && value !== this._filterResetToken) {
            this._filterResetToken = value;
            this.resetDateFilter(false);
        }
    }
    /** Applied once on init when set (e.g. Connect defaulting to Today) — same effect as the user picking this range themselves. */
    @Input() defaultDateRange: AncDatePickerValue | null = null;
    /**
     * Range the date filter lands on after "Clear all" instead of being emptied
     * (Connect always falls back to Today). Left null, Clear all clears the date
     * as before.
     */
    @Input() clearAllDateRange: AncDatePickerValue | null = null;

    @Output() chipChange = new EventEmitter<AncFilterChange>();
    @Output() chipOpen = new EventEmitter<string>();
    @Output() loadMore = new EventEmitter<string>();
    @Output() clearAll = new EventEmitter<void>();
    /** Emits API `created_at` (`YYYY-MM-DD,YYYY-MM-DD`) or `null` when cleared. */
    @Output() createdAtFilterChange = new EventEmitter<string | null>();
    /** Emits the raw applied range (or null when cleared) — e.g. so a parent can persist the user's actual selection across remounts. */
    @Output() dateRangeChange = new EventEmitter<AncDatePickerValue | null>();

    dateRangeValue: AncDatePickerValue | null = null;
    dateRangeActive = false;

    /** True while the chips would wrap onto a second line: they collapse into the drawer. */
    readonly collapsed = signal(false);
    /** Filter drawer (collapsed bar only). Closing only hides it; chip values stay with the parent. */
    readonly drawerOpen = signal(false);
    readonly drawerId = `anc-fb-drawer-${++nextFilterDrawerId}`;

    @ViewChild('bar', { static: true }) private bar?: ElementRef<HTMLElement>;
    @ViewChild('drawerTrigger') private drawerTrigger?: ElementRef<HTMLButtonElement>;

    private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
    private readonly injector = inject(Injector);
    private readonly destroyRef = inject(DestroyRef);
    private _filterResetToken = 0;
    private createdAtFilter: string | null = null;

    constructor(private readonly cdr: ChangeDetectorRef) {}

    // Chips, values (chip labels) and the applied count change the bar's width.
    ngOnChanges(): void {
        this.scheduleFitCheck();
    }

    ngAfterViewInit(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        // The bar is content-sized inside its filter row, so also watch a few ancestors:
        // the row resizes when the window or the side menu changes the page width.
        // Checked on the next frame: changing layout inside the observer callback would
        // trigger the browser's "ResizeObserver loop" error.
        let frame = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => this.updateCollapsed());
        });
        let element: HTMLElement | null = this.host.nativeElement;
        for (let level = 0; element && level < 4; level++, element = element.parentElement) {
            observer.observe(element);
        }
        this.destroyRef.onDestroy(() => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        });
    }

    openDrawer(): void {
        this.drawerOpen.set(true);
    }

    /** Closes the drawer and returns focus to the "Filters" button that opened it. */
    closeDrawer(): void {
        if (!this.drawerOpen()) {
            return;
        }
        this.drawerOpen.set(false);
        this.drawerTrigger?.nativeElement.focus();
    }

    ngOnInit(): void {
        if (this.defaultDateRange) {
            this.onDateRangeUpdated(this.defaultDateRange);
        }
    }

    /** Chip count plus one when a custom date range is active. */
    get totalAppliedCount(): number {
        return this.appliedCount + (this.createdAtFilter ? 1 : 0);
    }

    trackChipById(_i: number, chip: AncFilterChip): string {
        return chip.id;
    }

    onDateRangeUpdated(range: AncDatePickerValue): void {
        this.applyDateRange(range);
        this.createdAtFilterChange.emit(this.createdAtFilter);
        this.dateRangeChange.emit(this.dateRangeValue);
        this.cdr.markForCheck();
        this.scheduleFitCheck();
    }

    onClearAllClick(): void {
        if (!this.clearAllDateRange) {
            this.resetDateFilter(false);
            this.clearAll.emit();
            return;
        }
        // Seed the bar's own state first so the chip shows the fallback, but
        // re-emit the date only AFTER `clearAll` — the parents' clear handlers
        // null their own `ancCreatedAtFilter`, so emitting first would just be
        // wiped again.
        this.applyDateRange(this.clearAllDateRange);
        this.clearAll.emit();
        this.createdAtFilterChange.emit(this.createdAtFilter);
        this.dateRangeChange.emit(this.dateRangeValue);
        this.cdr.markForCheck();
    }

    private scheduleFitCheck(): void {
        afterNextRender(() => this.updateCollapsed(), { injector: this.injector });
    }

    /**
     * Lays the chips out inline for a moment and checks whether any of them wraps onto a
     * second line; collapses the bar into the Filters button if so. Runs synchronously
     * (no paint in between), so the trial layout is never visible.
     */
    private updateCollapsed(): void {
        const bar = this.bar?.nativeElement;
        if (!bar) {
            return;
        }
        let wraps = false;
        if (this.chips.length) {
            const wasCollapsed = bar.classList.contains('anc-fb--collapsed');
            bar.classList.remove('anc-fb--collapsed');
            wraps = this.inlineItemsWrap(bar);
            bar.classList.toggle('anc-fb--collapsed', wasCollapsed);
        }
        if (wraps !== this.collapsed()) {
            this.collapsed.set(wraps);
            if (!wraps) {
                this.drawerOpen.set(false);
            }
            this.cdr.markForCheck();
        }
    }

    /** True when an inline item (date range, chip, count, Clear all) starts below the first one. */
    private inlineItemsWrap(bar: HTMLElement): boolean {
        const items = Array.from(
            bar.querySelectorAll<HTMLElement>(
                ':scope > .anc-fb-chips > :not(.anc-fb-drawer-header), :scope > .anc-fb-count, :scope > .anc-fb-clear'
            )
        ).filter((item) => item.getClientRects().length > 0);
        if (items.length < 2) {
            return false;
        }
        const firstBottom = items[0].getBoundingClientRect().bottom;
        return items.some((item) => item.getBoundingClientRect().top >= firstBottom - 1);
    }

    /** Sets the bar's own date state, without emitting. */
    private applyDateRange(range: AncDatePickerValue): void {
        const createdAt = formatAncCreatedAtFilter(range);
        if (createdAt) {
            this.createdAtFilter = createdAt;
            this.dateRangeValue = range;
            this.dateRangeActive = true;
        } else {
            this.resetDateFilter(false);
        }
    }

    private resetDateFilter(emitChange: boolean): void {
        this.dateRangeValue = null;
        this.dateRangeActive = false;
        this.createdAtFilter = null;
        if (emitChange) {
            this.createdAtFilterChange.emit(null);
            this.dateRangeChange.emit(null);
        }
        this.cdr.markForCheck();
    }
}
